export interface KeyPair {
  publicKey: CryptoKey;
  privateKey: CryptoKey;
}

export interface CryptoUtil {
  encrypt(data: string, key: string): Promise<string>;
  decrypt(data: string, key: string): Promise<string>;
  exportKeyToPem(key: CryptoKey, type: "public" | "private"): Promise<string>;
  webGenerateRSAKeyPair(): Promise<KeyPair>;
  webDecryptRSA(privateKey: CryptoKey, encryptedText: string): Promise<string>;
}

function ensureWebCrypto(): Crypto {
  if (!globalThis.crypto?.subtle) {
    throw new Error("Web Crypto API is not available");
  }
  return globalThis.crypto;
}

function encodeUtf8(value: string): Uint8Array {
  return new TextEncoder().encode(value);
}

function decodeUtf8(bytes: Uint8Array): string {
  return new TextDecoder().decode(bytes);
}

function base64ToBytes(value: string): Uint8Array {
  const normalized = value.replace(/\s/g, "");
  const bin = atob(normalized);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) {
    bytes[i] = bin.charCodeAt(i);
  }
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1) {
    bin += String.fromCharCode(bytes[i]);
  }
  return btoa(bin);
}

function hexToBytes(value: string): Uint8Array {
  const bytes = new Uint8Array(value.length / 2);
  for (let i = 0; i < value.length; i += 2) {
    bytes[i / 2] = Number.parseInt(value.slice(i, i + 2), 16);
  }
  return bytes;
}

function asBufferSource(bytes: Uint8Array): BufferSource {
  return bytes as unknown as BufferSource;
}

function normalizeAesKey(inputKey: string): Uint8Array {
  const rawKey = inputKey.trim();

  const utf8Bytes = encodeUtf8(rawKey);
  if (utf8Bytes.length === 32) {
    return utf8Bytes;
  }

  if (/^[0-9a-fA-F]{64}$/.test(rawKey)) {
    return hexToBytes(rawKey);
  }

  try {
    const decoded = base64ToBytes(rawKey);
    if (decoded.length === 32) {
      return decoded;
    }
  } catch {
    // no-op: invalid base64 format
  }

  throw new Error("Invalid AES-256 key length. Expected 32-byte key (utf8/hex/base64).");
}

async function importAesKey(rawKey: string, usage: KeyUsage): Promise<CryptoKey> {
  const cryptoApi = ensureWebCrypto();
  const keyBytes = normalizeAesKey(rawKey);
  return cryptoApi.subtle.importKey("raw", asBufferSource(keyBytes), { name: "AES-GCM" }, false, [usage]);
}

class CryptoService implements CryptoUtil {
  async encrypt(data: string, key: string): Promise<string> {
    const cryptoApi = ensureWebCrypto();
    const aesKey = await importAesKey(key, "encrypt");
    const iv = cryptoApi.getRandomValues(new Uint8Array(12));
    const plainBytes = encodeUtf8(data);

    const encryptedBuffer = await cryptoApi.subtle.encrypt(
      { name: "AES-GCM", iv: asBufferSource(iv), tagLength: 128 },
      aesKey,
      asBufferSource(plainBytes)
    );

    const encryptedBytes = new Uint8Array(encryptedBuffer);
    if (encryptedBytes.length < 16) {
      throw new Error("Invalid encrypted payload");
    }

    const authTag = encryptedBytes.slice(encryptedBytes.length - 16);
    const cipherText = encryptedBytes.slice(0, encryptedBytes.length - 16);

    return `${bytesToBase64(iv)}.${bytesToBase64(authTag)}.${bytesToBase64(cipherText)}`;
  }

  async decrypt(data: string, key: string): Promise<string> {
    const parts = data.split(".");
    if (parts.length !== 3) {
      throw new Error("Invalid encrypted format. Expected iv.authTag.cipherText");
    }

    const iv = base64ToBytes(parts[0]);
    const authTag = base64ToBytes(parts[1]);
    const cipherText = base64ToBytes(parts[2]);

    if (iv.length !== 12 || authTag.length !== 16) {
      throw new Error("Invalid IV/AuthTag length for AES-GCM");
    }

    const encryptedPayload = new Uint8Array(cipherText.length + authTag.length);
    encryptedPayload.set(cipherText, 0);
    encryptedPayload.set(authTag, cipherText.length);

    const cryptoApi = ensureWebCrypto();
    const aesKey = await importAesKey(key, "decrypt");
    const decryptedBuffer = await cryptoApi.subtle.decrypt(
      { name: "AES-GCM", iv: asBufferSource(iv), tagLength: 128 },
      aesKey,
      asBufferSource(encryptedPayload)
    );

    return decodeUtf8(new Uint8Array(decryptedBuffer));
  }

  async exportKeyToPem(key: CryptoKey, type: "public" | "private"): Promise<string> {
    const cryptoApi = ensureWebCrypto();
    const format = type === "public" ? "spki" : "pkcs8";
    const exported = await cryptoApi.subtle.exportKey(format, key);
    const pemBody = bytesToBase64(new Uint8Array(exported));
    const pemHeader = type === "public" ? "PUBLIC KEY" : "PRIVATE KEY";
    return `-----BEGIN ${pemHeader}-----\n${pemBody}\n-----END ${pemHeader}-----`;
  }

  async webGenerateRSAKeyPair(): Promise<KeyPair> {
    const cryptoApi = ensureWebCrypto();
    const keyPair = await cryptoApi.subtle.generateKey(
      {
        name: "RSA-OAEP",
        modulusLength: 2048,
        publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
        hash: "SHA-256",
      },
      true,
      ["encrypt", "decrypt"]
    );

    return {
      publicKey: keyPair.publicKey,
      privateKey: keyPair.privateKey,
    };
  }

  async webDecryptRSA(privateKey: CryptoKey, encryptedText: string): Promise<string> {
    const cryptoApi = ensureWebCrypto();
    const encryptedBytes = base64ToBytes(encryptedText);
    const decryptedBuffer = await cryptoApi.subtle.decrypt(
      { name: "RSA-OAEP" },
      privateKey,
      asBufferSource(encryptedBytes)
    );
    return decodeUtf8(new Uint8Array(decryptedBuffer));
  }
}

export const cryptoUtil: CryptoUtil = new CryptoService();
